<template>
  <div class="agents-page bg-neutral-50 min-h-screen">
    <main class="container mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <!-- 头部 -->
      <div class="text-center mb-10">
        <div
          class="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium mb-4"
        >
          <i class="fa fa-bolt mr-1"></i>站内直连 · 无需跳转
        </div>
        <h1 class="text-3xl md:text-4xl font-bold mb-3">AI 智能体</h1>
        <p class="text-neutral-500 max-w-2xl mx-auto">
          点开就能聊，不用再打开外部平台链接。手机上同样直接在小程序里对话。
        </p>
        <button
          @click="openSubmit"
          class="mt-5 px-5 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:opacity-90"
        >
          <i class="fa fa-plus mr-1"></i>创建智能体
        </button>
      </div>

      <!-- 工具型智能体：科学数据分析台 -->
      <router-link
        to="/analysis"
        class="block mb-8 bg-white rounded-2xl border border-neutral-200 p-5 hover:shadow-lg hover:border-primary transition-all duration-300 group"
      >
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 rounded-xl bg-gradient-to-br from-violet-500 to-primary text-white flex items-center justify-center text-xl flex-shrink-0"
          >
            📊
          </div>
          <div class="flex-1 min-w-0">
            <h3
              class="font-bold text-neutral-900 group-hover:text-primary transition-colors"
            >
              小义AI科学分析台
            </h3>
            <p class="text-sm text-neutral-500 mt-0.5">
              上传实验图片自动识别数据，绘制三组变化曲线、算平均值并预测下一个值
            </p>
          </div>
          <i
            class="fa fa-arrow-right text-neutral-300 group-hover:text-primary transition-colors flex-shrink-0"
          ></i>
        </div>
      </router-link>

      <!-- 教师知识智能体（审核通过后进入广场） -->
      <div v-if="userAgents.length" class="mb-8">
        <div class="flex items-end justify-between mb-3">
          <div>
            <h2 class="text-lg font-bold text-neutral-900">
              <i class="fa fa-book text-emerald-500 mr-1"></i>教师知识智能体
            </h2>
            <p class="text-xs text-neutral-500 mt-0.5">
              老师基于自己的教案与资料创建，已通过审核，点开即可对话
            </p>
          </div>
          <router-link
            to="/knowledge?tab=square"
            class="text-xs text-primary hover:underline flex-shrink-0"
          >
            查看全部 <i class="fa fa-angle-right"></i>
          </router-link>
        </div>
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          <router-link
            v-for="a in userAgents"
            :key="a.id"
            :to="`/knowledge?tab=square&open=${a.id}`"
            class="bg-white rounded-2xl border border-neutral-200 p-4 hover:shadow-lg hover:border-primary transition-all duration-300 flex flex-col group"
          >
            <div class="flex items-start gap-3 mb-2">
              <div
                class="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold flex-shrink-0 overflow-hidden"
                :style="{ background: colorOf(a.id) }"
              >
                <img v-if="a.avatar" :src="a.avatar" class="w-full h-full object-cover" alt="" />
                <span v-else>{{ (a.name || "智")[0] }}</span>
              </div>
              <div class="min-w-0 flex-1">
                <h3 class="font-bold text-sm text-neutral-900 line-clamp-1 group-hover:text-primary">
                  {{ a.name }}
                </h3>
                <p class="text-[11px] text-neutral-400 mt-0.5 truncate">
                  {{ a.creatorName || "老师" }} · {{ a.docCount || 0 }} 篇资料
                </p>
              </div>
            </div>
            <p class="text-xs text-neutral-500 line-clamp-2 flex-1">
              {{ a.intro || "暂无简介" }}
            </p>
          </router-link>
        </div>
      </div>

      <!-- 加载中 -->
      <div v-if="loading" class="text-center py-20">
        <i class="fa fa-spinner fa-spin text-primary text-3xl"></i>
      </div>

      <!-- 空状态 -->
      <div
        v-else-if="agents.length === 0"
        class="text-center py-20 bg-white rounded-2xl border border-neutral-200"
      >
        <div
          class="inline-flex w-16 h-16 rounded-full bg-neutral-100 items-center justify-center mb-4"
        >
          <i class="fa fa-robot text-neutral-400 text-2xl"></i>
        </div>
        <h3 class="text-lg font-medium mb-1">还没有智能体</h3>
        <p class="text-neutral-500 text-sm">
          管理员可在「管理后台 → 智能体管理」中添加扣子 / workbuddy 等平台的智能体。
        </p>
      </div>

      <!-- 智能体卡片 -->
      <div
        v-else
        class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5"
      >
        <div
          v-for="a in agents"
          :key="a.id"
          class="relative bg-white rounded-2xl border border-neutral-200 p-5 hover:shadow-lg hover:border-primary transition-all duration-300 cursor-pointer flex flex-col group"
          @click="goChat(a)"
        >
          <button
            @click.stop="toggleFav(a)"
            class="absolute top-3 right-3 z-10 w-8 h-8 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-sm transition-colors"
            :class="isFav(a.id) ? 'text-primary bg-primary/10' : 'text-neutral-400 hover:text-primary'"
            :title="isFav(a.id) ? '已加入我的应用，点击移出' : '加入我的应用'"
          >
            <i :class="isFav(a.id) ? 'fa fa-check' : 'fa fa-plus'"></i>
          </button>
          <div class="flex items-start mb-3">
            <div
              class="w-14 h-14 rounded-2xl bg-gradient-to-br from-primary to-blue-400 flex items-center justify-center text-white text-xl font-bold flex-shrink-0 overflow-hidden"
            >
              <img
                v-if="a.avatar"
                :src="a.avatar"
                class="w-full h-full object-cover"
              />
              <span v-else>{{ firstChar(a.name) }}</span>
            </div>
            <div class="ml-3 min-w-0 flex-1">
              <h3
                class="font-bold text-base mb-1 group-hover:text-primary transition-colors line-clamp-1"
              >
                {{ a.name }}
              </h3>
              <p class="text-xs text-neutral-400">
                {{ a.useCount || 0 }} 次对话
              </p>
            </div>
          </div>

          <p class="text-sm text-neutral-500 line-clamp-3 flex-1 mb-3">
            {{ a.intro || "暂无介绍" }}
          </p>

          <div v-if="a.tagList && a.tagList.length" class="flex flex-wrap gap-1 mb-4">
            <span
              v-for="(t, i) in a.tagList"
              :key="i"
              class="px-2 py-0.5 bg-neutral-100 text-neutral-600 rounded text-xs"
              >{{ t }}</span
            >
          </div>

          <button
            class="w-full py-2 rounded-xl bg-primary/10 text-primary font-medium text-sm group-hover:bg-primary group-hover:text-white transition-colors"
          >
            开始对话 <i class="fa fa-arrow-right ml-1 text-xs"></i>
          </button>
        </div>
      </div>
    </main>

    <!-- 创建智能体（投稿，需审核） -->
    <div
      v-if="submitVisible"
      class="fixed inset-0 z-50 flex items-start justify-center bg-black/40 overflow-y-auto py-8 px-4"
    >
      <div class="bg-white rounded-2xl w-full max-w-2xl p-6 shadow-xl">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-lg font-bold text-neutral-900">创建智能体</h2>
          <button class="text-neutral-400 hover:text-neutral-600" @click="submitVisible = false">
            <i class="fa fa-times"></i>
          </button>
        </div>
        <p class="text-xs text-neutral-500 mb-4">
          提交后进入审核，通过后会出现在 AI 智能体页；审核意见会通过站内消息发给你。
        </p>

        <div class="space-y-3">
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">名称 *</label>
              <input v-model="form.name" maxlength="40" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">标签（逗号分隔）</label>
              <input v-model="form.tags" placeholder="备课,作文批改" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
            </div>
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">一句话简介</label>
            <textarea v-model="form.intro" rows="2" maxlength="200" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary resize-none"></textarea>
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">头像地址（可选）</label>
            <input v-model="form.avatar" placeholder="https://..." class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
          </div>

          <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">平台</label>
              <select v-model="form.platform" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:border-primary">
                <option value="openai">OpenAI 兼容（DeepSeek / 通义等）</option>
                <option value="coze">扣子 Coze</option>
              </select>
            </div>
            <div>
              <label class="block text-xs font-medium text-neutral-600 mb-1">
                {{ form.platform === "coze" ? "Bot ID *" : "模型名 *" }}
              </label>
              <input
                v-model="form.botId"
                :placeholder="form.platform === 'coze' ? '扣子 Bot ID' : 'deepseek-chat'"
                class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary"
              />
            </div>
          </div>

          <div v-if="form.platform === 'openai'">
            <label class="block text-xs font-medium text-neutral-600 mb-1">接口地址 *</label>
            <input v-model="form.apiBase" placeholder="https://api.deepseek.com/v1" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">API Key *</label>
            <input v-model="form.apiToken" type="password" autocomplete="new-password" placeholder="只保存在服务端，不会下发给访客" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
          </div>

          <div>
            <label class="block text-xs font-medium text-neutral-600 mb-1">开场白（可选）</label>
            <input v-model="form.welcome" placeholder="你好，我是…" class="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm focus:outline-none focus:border-primary" />
          </div>
        </div>

        <div class="flex justify-end gap-2 mt-6">
          <button class="px-4 py-2 rounded-lg border border-neutral-200 text-sm text-neutral-600" @click="submitVisible = false">取消</button>
          <button
            class="px-5 py-2 rounded-lg bg-primary text-white text-sm font-medium disabled:opacity-50"
            :disabled="saving"
            @click="save"
          >
            {{ saving ? "提交中..." : "提交审核" }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from "element-plus";
import { requireLogin } from "../utils/authGuard";

export default {
  name: "AgentSquare",
  data() {
    return {
      agents: [],
      /** 教师自建、审核通过的知识智能体 */
      userAgents: [],
      loading: false,
      submitVisible: false,
      saving: false,
      form: {
        id: "",
        name: "",
        avatar: "",
        intro: "",
        tags: "",
        platform: "openai",
        apiBase: "https://api.deepseek.com/v1",
        botId: "",
        apiToken: "",
        welcome: "",
        chatMode: "api",
      },
      /** 当前用户已收藏的智能体 id */
      favIds: [],
    };
  },
  created() {
    this.getAgents();
    this.loadFavIds();
  },
  methods: {
    /** 打开创建智能体弹层（需登录） */
    openSubmit() {
      if (!requireLogin(this, "创建智能体")) return;
      this.form = {
        id: "",
        name: "",
        avatar: "",
        intro: "",
        tags: "",
        platform: "openai",
        apiBase: "https://api.deepseek.com/v1",
        botId: "",
        apiToken: "",
        welcome: "",
        chatMode: "api",
      };
      this.submitVisible = true;
    },

    async save() {
      if (!this.form.name.trim()) {
        ElMessage.warning("请填写名称");
        return;
      }
      if (!this.form.botId.trim()) {
        ElMessage.warning(
          this.form.platform === "coze" ? "请填写 Bot ID" : "请填写模型名"
        );
        return;
      }
      if (!this.form.apiToken.trim()) {
        ElMessage.warning("请填写 API Key");
        return;
      }
      if (this.form.platform === "openai" && !this.form.apiBase.trim()) {
        ElMessage.warning("请填写接口地址");
        return;
      }
      this.saving = true;
      try {
        const res = await this.$axios({
          url: "agent/submit",
          method: "PUT",
          data: { data: await this.$aesEncrypt(this.form) },
        });
        if (res.code === 200) {
          ElMessage.success("已提交，等待审核");
          this.submitVisible = false;
        } else {
          ElMessage.error(res.msg || "提交失败");
        }
      } catch (e) {
        ElMessage.error("提交失败，请稍后重试");
      }
      this.saving = false;
    },

    async loadFavIds() {
      if (!localStorage.getItem("token")) return;
      try {
        const res = await this.$axios({ url: "fav/ids/agent" });
        if (res.code === 200) this.favIds = res.data || [];
      } catch (e) {
        /* 未登录时静默 */
      }
    },
    isFav(id) {
      return this.favIds.indexOf(id) >= 0;
    },
    /** 加入 / 移出我的应用 */
    toggleFav(a) {
      if (!requireLogin(this, "加入我的应用")) return;
      const on = this.isFav(a.id);
      this.$axios({
        url: "fav/agent/" + a.id,
        method: on ? "DELETE" : "GET",
      })
        .then((res) => {
          if (res.code !== 200) {
            ElMessage.error(res.msg || "操作失败");
            return;
          }
          if (on) {
            this.favIds = this.favIds.filter((x) => x !== a.id);
            ElMessage.success("已从我的应用移出");
          } else {
            this.favIds.push(a.id);
            ElMessage.success("已加入我的应用，可在个人板块查看");
          }
        })
        .catch(() => ElMessage.error("操作失败，请稍后重试"));
    },
    getAgents() {
      this.loading = true;
      this.$axios({ url: "agent/list" })
        .then((res) => {
          this.agents = res.data || [];
        })
        .catch(() => {})
        .finally(() => {
          this.loading = false;
        });
      // 教师知识智能体：后端只返回审核通过且已公开的
      this.$axios({ url: "user/agent/list" })
        .then((res) => {
          this.userAgents = (res.data && res.data.list) || [];
        })
        .catch(() => {
          this.userAgents = [];
        });
    },
    goChat(agent) {
      this.$router.push("/agent/" + agent.id);
    },
    firstChar(name) {
      return name ? name.trim().charAt(0) : "A";
    },
    colorOf(id) {
      const palette = ["#6366f1", "#0ea5e9", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6"];
      let h = 0;
      const s = String(id || "");
      for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 9973;
      return palette[h % palette.length];
    },
  },
};
</script>
